/**
 * API helpers for Oelala frontend
 * Includes authenticated requests using Supabase JWT
 */

import { BACKEND_BASE, DEBUG, STORAGE_BASE } from './config'
import { supabase } from './lib/supabase'

const EXTRA_ALLOWED_ORIGINS = [
  'https://storage.oelala.xyz',
  'https://storage2.oelala.xyz',
]

function getBrowserOrigin() {
  return typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : BACKEND_BASE
}

function getAllowedOrigins() {
  const origins = new Set(EXTRA_ALLOWED_ORIGINS)
  const browserOrigin = getBrowserOrigin()
  try {
    origins.add(new URL(BACKEND_BASE, browserOrigin).origin)
    origins.add(new URL(STORAGE_BASE, browserOrigin).origin)
    origins.add(browserOrigin)
  } catch (e) {
    console.error('🔐 API: failed to build allowed origins', e)
  }
  return origins
}

function resolveAllowedApiUrl(endpoint) {
  const rawEndpoint = String(endpoint || '')
  const browserOrigin = getBrowserOrigin()
  const backendUrl = new URL(BACKEND_BASE, browserOrigin)
  const resolvedUrl = new URL(rawEndpoint, backendUrl)

  if (!getAllowedOrigins().has(resolvedUrl.origin)) {
    throw new Error('Blocked cross-origin API request')
  }

  return resolvedUrl
}

function shouldAttachAuth(url) {
  const browserOrigin = getBrowserOrigin()
  const backendOrigin = new URL(BACKEND_BASE, browserOrigin).origin
  return url.origin === backendOrigin || url.origin === browserOrigin
}

/**
 * Get the current session's access token
 */
export async function getAccessToken() {
  try {
    if (!supabase) {
      console.log('🔐 API: supabase client not available')
      return null
    }
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) {
      console.error('🔐 API: getSession error:', error)
      return null
    }
    if (session?.access_token) {
      console.log('🔐 API: Got access token for user:', session.user?.email)
    } else {
      console.log('🔐 API: No active session')
    }
    return session?.access_token || null
  } catch (e) {
    console.error('🔐 API: getAccessToken exception:', e)
    return null
  }
}

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., '/user/media')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>}
 */
export async function apiFetch(endpoint, options = {}) {
  const token = await getAccessToken()
  const url = resolveAllowedApiUrl(endpoint)

  const headers = {
    ...options.headers,
  }

  if (token && shouldAttachAuth(url)) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Don't set Content-Type for FormData (browser sets it with boundary)
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    if (options.body) {
      headers['Content-Type'] = 'application/json'
    }
  }

  // Bust Cloudflare cache for static media files that may have been cached
  // without CORS headers (before the Vary: Origin fix was deployed)
  if (url.pathname.includes('/comfyui/output/') || url.pathname.includes('/media/generated/')) {
    url.searchParams.set('_cors', '1')
  }

  if (DEBUG) {
    console.log('🔐 API request', {
      method: options.method || 'GET',
      endpoint: url.pathname,
      authenticated: Boolean(token),
    })
  }

  return fetch(url.toString(), {
    ...options,
    headers,
    credentials: 'same-origin',
  })
}

// ============================================================================
// Legacy helpers (kept for backwards compatibility)
// ============================================================================

// Lightweight API helper with graceful JSON parsing fallback
export async function postForm(url, formData, headers = {}) {
  const res = await apiFetch(url, {
    method: 'POST',
    body: formData,
    headers,
  })

  const text = await res.text()
  let data
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    // Fallback: return raw text when JSON parsing fails
    data = text
  }

  // Check for insufficient credits error (402)
  if (res.status === 402 && data?.detail) {
    // Validate that we have all required properties for insufficient credits
    if (typeof data.detail === 'object' &&
        data.detail.error === 'insufficient_credits' &&
        typeof data.detail.required === 'number' &&
        typeof data.detail.available === 'number') {
      // Dispatch custom event that CreditsContext can listen to
      window.dispatchEvent(new CustomEvent('insufficient-credits', {
        detail: {
          required: data.detail.required,
          available: data.detail.available,
          packages: Array.isArray(data.detail.packages) ? data.detail.packages : []
        }
      }))
    }
  }

  // Normalize detail error string to prevent UI [object Object] breaking
  if (!res.ok && data && typeof data.detail === 'object') {
    const orig = data.detail;
    if (orig.error === 'insufficient_credits') {
      data.detail_obj = orig;
      data.detail = "Not enough credits. (Need " + orig.required + ", have " + orig.available + ")";
    } else {
      data.detail = JSON.stringify(orig);
    }
  }

  return { ok: res.ok, status: res.status, data }
}

export async function getJson(url) {
  const res = await apiFetch(url, { method: 'GET' })
  const text = await res.text()
  try {
    const data = text ? JSON.parse(text) : null
    return { ok: res.ok, status: res.status, data }
  } catch {
    return { ok: res.ok, status: res.status, data: text }
  }
}

export async function postJson(url, body = {}) {
  const res = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })
  const text = await res.text()
  try {
    const data = text ? JSON.parse(text) : null

    if (res.status === 402 && data?.detail) {
      if (typeof data.detail === 'object' && data.detail.error === 'insufficient_credits') {
        window.dispatchEvent(new CustomEvent('insufficient-credits', {
          detail: {
            required: data.detail.required,
            available: data.detail.available,
            packages: Array.isArray(data.detail.packages) ? data.detail.packages : []
          }
        }))
      }
    }

    if (!res.ok && data && typeof data.detail === 'object') {
      const orig = data.detail;
      if (orig.error === 'insufficient_credits') {
        data.detail_obj = orig;
        data.detail = "Not enough credits. (Need " + orig.required + ", have " + orig.available + ")";
      } else {
        data.detail = JSON.stringify(orig);
      }
    }

    return { ok: res.ok, status: res.status, data }
  } catch {
    return { ok: res.ok, status: res.status, data: text }
  }
}

// ============================================================================
// New authenticated API helpers
// ============================================================================

/**
 * GET request with auth
 */
export async function apiGet(endpoint) {
  const response = await apiFetch(endpoint, { method: 'GET' })
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

/**
 * POST request with auth
 */
export async function apiPost(endpoint, data) {
  const body = data instanceof FormData ? data : JSON.stringify(data)
  const response = await apiFetch(endpoint, {
    method: 'POST',
    body,
  })
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

/**
 * DELETE request with auth
 */
export async function apiDelete(endpoint) {
  const response = await apiFetch(endpoint, { method: 'DELETE' })
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

// ============================================================================
// User Media API helpers
// ============================================================================

/**
 * List user's media files from storage
 * @param {string} type - 'all', 'video', 'image', 'audio'
 */
export async function listUserMedia(type = 'all') {
  return apiGet(`/user/media?type=${type}`)
}

/**
 * Unified media listing - aggregates user, generated, comfyui-local, and public sources
 * @param {string} type - 'all', 'video', 'image', 'audio'
 * @param {string} source - 'all', 'user', 'generated', 'comfyui-local', 'public'
 * @param {object} adminFilters - Admin-only filters: { filterUserId, includeAllUsers }
 */
export async function listUnifiedMedia(type = 'all', source = 'all', adminFilters = {}) {
  const params = new URLSearchParams({ type, source })

  // Admin-only filters
  if (adminFilters.filterUserId) {
    params.append('filter_user_id', adminFilters.filterUserId)
  }
  if (adminFilters.includeAllUsers) {
    params.append('include_all_users', 'true')
  }

  return apiGet(`/api/media/unified?${params.toString()}`)
}

/**
 * Build authenticated URL for user media
 * Note: For direct access, use the proxy endpoint which checks auth
 */
export function getUserMediaUrl(mediaType, filename) {
  return `${BACKEND_BASE}/user/media/${mediaType}/${encodeURIComponent(filename)}`
}

/**
 * Upload media to user's storage
 * @param {string} mediaType - 'images', 'videos', 'audio'
 * @param {File} file - File to upload
 * @param {function} onProgress - Progress callback (0-100)
 */
export async function uploadUserMedia(mediaType, file, onProgress = null) {
  const token = await getAccessToken()

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    formData.append('file', file)

    xhr.open('POST', `${BACKEND_BASE}/user/media/${mediaType}`)

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100))
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          resolve({ success: true })
        }
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`))
      }
    }

    xhr.onerror = () => reject(new Error('Upload failed'))
    xhr.send(formData)
  })
}

/**
 * Delete user's media file
 */
export async function deleteUserMedia(mediaType, filename) {
  return apiDelete(`/user/media/${mediaType}/${encodeURIComponent(filename)}`)
}

/**
 * Get user profile
 */
export async function getUserProfile() {
  return apiGet('/user/profile')
}

// ============================================================================
// Gallery API helpers
// ============================================================================

/**
 * Publish media to gallery
 * @param {Object} data - Publication data (title, description, tags, etc.)
 */
export async function publishToGallery(data) {
  return apiPost('/api/gallery/publish', data)
}

/**
 * Unpublish media from gallery
 * @param {string} mediaId - Published media ID
 */
export async function unpublishFromGallery(mediaId) {
  return apiDelete(`/api/gallery/${mediaId}`)
}

/**
 * List gallery items
 * @param {Object} filters - Filter options (media_type, is_nsfw, sort_by, page, per_page)
 */
export async function listGallery(filters = {}) {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, value.toString())
    }
  })
  return apiGet(`/api/gallery?${params.toString()}`)
}

/**
 * Get single gallery item details
 * @param {string} mediaId - Published media ID
 */
export async function getGalleryItem(mediaId) {
  return apiGet(`/api/gallery/${mediaId}`)
}

/**
 * Toggle like on gallery item
 * @param {string} mediaId - Published media ID
 */
export async function toggleGalleryLike(mediaId) {
  return apiPost(`/api/gallery/${mediaId}/like`, {})
}

/**
 * Get user's published items
 * @param {string} userId - User ID
 * @param {number} page - Page number
 * @param {number} perPage - Items per page
 */
export async function getUserPublishedMedia(userId, page = 1, perPage = 30) {
  return apiGet(`/api/gallery/users/${userId}?page=${page}&per_page=${perPage}`)
}
